/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import { ReactNode, useCallback, useMemo } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import useWindowSize from 'src/hooks/useWindowSize'
import Text from 'src/components/ts-components/Text'

type Props = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  /**
   * Body text. Use a string for simple text or a ReactNode for multi-line inline content —
   * it is set inside a paragraph, so block content (a table, a list) goes in `children`.
   */
  description?: ReactNode
  /** Block content (a table, a list), rendered below the description in its own container. */
  children?: ReactNode
  cancelLabel?: string
  confirmLabel?: string
  /** Label rendered on the confirm button while a request is in flight. */
  loadingLabel?: string
  isLoading?: boolean
  /** Render the confirm button in destructive (red) style. Defaults to primary (yellow). */
  destructive?: boolean
  /**
   * Drop the cancel button, leaving the confirm button alone.
   *
   * For an ACKNOWLEDGEMENT rather than a choice — something already decided
   * that the reader only needs to be told about. Offering "Cancel" there would
   * imply the outcome could be declined when it cannot.
   */
  hideCancel?: boolean
}

const WarningModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Warning! Before You Proceed',
  description,
  cancelLabel = 'CANCEL',
  confirmLabel = 'SAVE',
  loadingLabel,
  isLoading = false,
  destructive = false,
  hideCancel = false,
  children,
}: Props) => {
  const confirmBg = destructive ? colors.ERROR_300 : colors.PRIMARY
  /**
   * Content on the confirm button, following its fill.
   *
   * The PRIMARY fill is tenant-switched (SportNinja amber, Canlan blue), so its
   * text has to switch too — `PRIMARY_BUTTON_CONTENT` is the shared config that
   * decides. Hardcoding white here made the label unreadable on the amber.
   *
   * The destructive fill is a fixed red that white always sits correctly on, and
   * the rule covers a solid PRIMARY fill only — so it keeps white.
   */
  const confirmContentColor = destructive
    ? colors.WHITE
    : PRIMARY_BUTTON_CONTENT.text
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_400};
          border: 1px solid ${colors.SECONDARY_300};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_400,
          padding: 0,
        },
        wrapper: {
          zIndex: 10012,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10011,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.WARNING_300};
          `}
        />

        <Text
          variant='heading3'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 32px;
            font-weight: 600;
            line-height: 38.4px;
            text-align: center;
          `}
        >
          {title}
        </Text>

        {description === undefined ? null : (
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
              style={`
                line-height: 23.2px;
              `}
            >
              {description}
            </Text>
          </div>
        )}

        {children === undefined ? null : (
          <div
            css={css`
              align-self: stretch;
            `}
          >
            {children}
          </div>
        )}

        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_300};
          `}
        />

        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          {/* Rendered conditionally rather than hidden: the button sets its own
              `display: flex`, which outranks the `[hidden]` attribute's
              `display: none`, so hiding it that way left it on screen. */}
          {hideCancel ? null : (
            <button
              css={css`
                flex: 1 1 0;
                padding: 10px 26px;
                border-radius: 8px;
                border: 2px solid ${colors.WHITE};
                background: transparent;
                justify-content: center;
                align-items: center;
                gap: 8px;
                display: flex;
                cursor: pointer;
                transition: all 0.2s;
                &:hover {
                  background: rgba(255, 255, 255, 0.1);
                }
              `}
              onClick={onClose}
              disabled={isLoading}
            >
              <Text
                variant='heading6'
                weight='semiBold'
                color={colors.WHITE}
                style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
              >
                {cancelLabel}
              </Text>
            </button>
          )}
          <button
            css={css`
              flex: 1 1 0;
              padding: 10px 26px;
              background: ${confirmBg};
              border-radius: 8px;
              border: 2px solid ${confirmBg};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.5 : 1};
              transition: all 0.2s;
              &:hover {
                opacity: ${isLoading ? 0.5 : 0.8};
              }
            `}
            onClick={isLoading ? undefined : onConfirm}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={confirmContentColor}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading && loadingLabel ? loadingLabel : confirmLabel}
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default WarningModal
